import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './SkillsVisualization.css';

const SkillsVisualization = ({ skills }) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [hoveredSkill, setHoveredSkill] = useState(null);

    const categories = [...new Set(skills.map(skill => skill.category))];

    const getSkillColor = (level) => {
        if (level >= 90) return '#2ecc71';
        if (level >= 70) return '#3498db';
        if (level >= 50) return '#f1c40f';
        return '#e74c3c';
    };

    const skillVariants = {
        hidden: { scale: 0, opacity: 0 },
        visible: i => ({
            scale: 1,
            opacity: 1,
            transition: {
                delay: i * 0.1,
                duration: 0.5,
                type: 'spring',
                stiffness: 100
            }
        }),
        hover: {
            scale: 1.1,
            transition: {
                duration: 0.3
            }
        }
    };

    const filteredSkills = selectedCategory
        ? skills.filter(skill => skill.category === selectedCategory)
        : skills;

    return (
        <div className="skills-visualization">
            <motion.div 
                className="categories-container"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <motion.button
                    className={`category-button ${!selectedCategory ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(null)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    All Skills
                </motion.button>
                {categories.map(category => (
                    <motion.button
                        key={category}
                        className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {category}
                    </motion.button>
                ))}
            </motion.div>

            <motion.div 
                className="skills-grid"
                layout
            >
                <AnimatePresence>
                    {filteredSkills.map((skill, i) => (
                        <motion.div
                            key={skill.name}
                            className="skill-card"
                            custom={i}
                            initial="hidden"
                            animate="visible"
                            variants={skillVariants}
                            whileHover="hover"
                            onHoverStart={() => setHoveredSkill(skill)}
                            onHoverEnd={() => setHoveredSkill(null)}
                            style={{
                                borderColor: getSkillColor(skill.level)
                            }}
                        >
                            <div className="skill-icon">
                                {skill.icon}
                            </div>
                            <h3>{skill.name}</h3>
                            <div className="skill-level-container">
                                <motion.div 
                                    className="skill-level"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${skill.level}%` }}
                                    style={{ backgroundColor: getSkillColor(skill.level) }}
                                />
                            </div>
                            <span className="skill-percentage">{skill.level}%</span>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </motion.div>

            <AnimatePresence>
                {hoveredSkill && (
                    <motion.div
                        className="skill-tooltip"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                    >
                        <h4>{hoveredSkill.name}</h4>
                        <p>{hoveredSkill.description}</p>
                        {hoveredSkill.projects && (
                            <div className="skill-projects">
                                <h5>Related Projects:</h5>
                                <ul>
                                    {hoveredSkill.projects.map(project => (
                                        <li key={project}>{project}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SkillsVisualization; 